// src/modules/app-views/omnichannel/pages/Init/components/TranslateLanguageSelect.jsx
// *****************************************************************************
// IMPORTS
// *****************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Select, Flex, Tooltip, theme } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { getCountryMetadata } from 'utils/phone';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';

// *****************************************************************************
// CONSTANTS
// *****************************************************************************

export const TRANSLATE_LANGUAGES = [
  { lang: 'pt', country: 'BR' },
  { lang: 'en', country: 'US' },
  { lang: 'es', country: 'ES' },
  { lang: 'fr', country: 'FR' },
  { lang: 'de', country: 'DE' },
  { lang: 'it', country: 'IT' },
  { lang: 'zh', country: 'CN' },
  { lang: 'ja', country: 'JP' },
  { lang: 'ko', country: 'KR' },
  { lang: 'ar', country: 'SA' },
  { lang: 'ru', country: 'RU' },
  { lang: 'hi', country: 'IN' },
  { lang: 'tr', country: 'TR' },
  { lang: 'nl', country: 'NL' },
  { lang: 'pl', country: 'PL' },
  { lang: 'uk', country: 'UA' },
  { lang: 'he', country: 'IL' },
  { lang: 'th', country: 'TH' },
  { lang: 'vi', country: 'VN' },
  { lang: 'id', country: 'ID' },
];

// *****************************************************************************
// COMPONENT
// *****************************************************************************

const TranslateLanguageSelect = ({ value, onChange, disabled, style, className, variant, noTooltip }) => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const storedLanguage = useZustand('theme', 'language', false);
  const { token } = theme.useToken();

  // 4. LOCAL STATE
  // ANTD v6 fix: controlled open state ensures dropdown closes on first click
  const [open, setOpen] = useState(false);

  // 5. LOCAL VARIABLES II
  const displayLang = useMemo(() => resolveDisplayLang(storedLanguage), [storedLanguage]);

  const options = useMemo(() => {
    const noTranslateOption = {
      value: 'none',
      label: (
        <Flex align="center" gap={8}>
          <StopOutlined style={{ fontSize: 16, opacity: 0.55, flexShrink: 0 }} />
          <span style={{ fontSize: 13 }}>Não traduzir</span>
        </Flex>
      ),
      searchLabel: 'nao traduzir no translate',
    };

    const langOptions = TRANSLATE_LANGUAGES.map(({ lang, country }) => {
      const meta = getCountryMetadata(country, displayLang);
      const countryName = meta?.names?.[displayLang] || meta?.names?.en || country;

      return {
        value: lang,
        label: (
          <Flex align="center" gap={8}>
            <span style={{ flexShrink: 0 }}>{renderFlag(meta, country)}</span>
            <span style={{ fontSize: 13 }}>{countryName}</span>
            <span style={{ fontSize: 11, color: token.colorTextTertiary, marginLeft: 'auto' }}>{lang}</span>
          </Flex>
        ),
        searchLabel: `${countryName} ${lang}`.toLowerCase(),
      };
    });

    return [noTranslateOption, ...langOptions];
  }, [displayLang, token]);

  // 6. HANDLERS
  // ANTD v6 fix: close dropdown on select
  const handleSelect = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpenChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  // 9. UI
  const selectNode = (
    <Select
      value={value ?? 'none'}
      onChange={onChange}
      disabled={disabled}
      open={open}
      onSelect={handleSelect}
      onOpenChange={handleOpenChange}
      className={className}
      variant={variant}
      style={{ width: 60, height: 40, ...style }}
      popupMatchSelectWidth={false}
      showSearch
      filterOption={(input, option) => {
        const search = (option?.searchLabel || '').toLowerCase();
        return search.includes(input.toLowerCase());
      }}
      labelRender={labelProps => {
        if (!labelProps.value || labelProps.value === 'none') {
          return (
            <Flex align="center" justify="center" style={{ height: '100%' }}>
              <StopOutlined style={{ fontSize: 16, opacity: 0.45 }} />
            </Flex>
          );
        }
        const entry = TRANSLATE_LANGUAGES.find(l => l.lang === labelProps.value);
        if (!entry) return labelProps.label;
        const meta = getCountryMetadata(entry.country);
        return (
          <Flex align="center" justify="center" style={{ height: '100%' }}>
            {renderFlag(meta, entry.country)}
          </Flex>
        );
      }}
      options={options}
    />
  );

  return noTooltip ? selectNode : (
    <Tooltip title={translateX('translate_conversation')}>{selectNode}</Tooltip>
  );
};

// *****************************************************************************
// EXPORT
// *****************************************************************************

export default TranslateLanguageSelect;

// *****************************************************************************
// HELPERS
// *****************************************************************************

export function renderFlag(meta, countryCode, style = {}) {
  return (
    <img
      src={`/img/flags/${(countryCode || '').toUpperCase()}.svg`}
      alt={countryCode}
      style={{ width: 22, height: 15, objectFit: 'cover', ...style }}
      onError={e => { e.target.style.display = 'none'; }}
    />
  );
}

function resolveDisplayLang(storedLanguage) {
  if (!storedLanguage) return 'en';
  return storedLanguage.split(/[-_]/)[0];
}
